import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminGetAiCredits, adminGetPlatformStats, adminGetSchools, adminUpdateSchool, getAdminSchoolAIUsage } from '../../utils/remote';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useSortableTable, SortColumn } from '../../school_panel/lib/useSortableTable';
import { Button, Chip, EmptyState, ErrorPanel, FormField, Initials, KpiCard, LoadingState, Modal, PagedFooter, PageHeader, PanelCard, Segmented, SortableTh, Table, Td, Track } from '../../school_panel/components/ui';
import usePagination from '../../school_panel/lib/usePagination';
import { apiErrorText } from '../../school_panel/lib/apiError';
import { lastSeen } from '../../school_panel/pages/dashboards/sections/format';
import { Bot, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { ADMIN_SCHOOL_PLANS } from '../../types/admin';
import type { AdminAiCreditsSchool, AdminSchoolAIUsageRow, AdminSchoolPlan } from '../../types/admin';
import { timeOf } from '../../school_panel/lib/dates';

// Each row is one user's AI usage record. Default sort is User A->Z, the
// closest analog to the Name-column convention used elsewhere.
const USER_COLUMNS: SortColumn<AdminSchoolAIUsageRow>[] = [
  { key: 'user', accessor: (u) => u.username },
  { key: 'email', accessor: (u) => u.email },
  { key: 'input_tokens', accessor: (u) => u.input_tokens, type: 'number' },
  { key: 'output_tokens', accessor: (u) => u.output_tokens, type: 'number' },
  { key: 'total_spent', accessor: (u) => u.total_spent_usd, type: 'number' },
  { key: 'last_usage', accessor: (u) => u.updated, type: 'date' },
];

const HIGH_USE = 90;
const pctUsed = (s: AdminAiCreditsSchool) => (s.allocation > 0 ? (s.credits_used / s.allocation) * 100 : 0);

const SCHOOL_COLUMNS: SortColumn<AdminAiCreditsSchool>[] = [
  { key: 'school', accessor: (s) => s.name },
  { key: 'used', accessor: (s) => pctUsed(s), type: 'number' },
  { key: 'input_tokens', accessor: (s) => s.input_tokens_total ?? 0, type: 'number' },
  { key: 'output_tokens', accessor: (s) => s.output_tokens_total ?? 0, type: 'number' },
  { key: 'spent', accessor: (s) => Number(s.spent_usd), type: 'number' },
  { key: 'last_usage', accessor: (s) => s.last_usage, type: 'date' },
];

const PLAN_LABEL: Record<AdminSchoolPlan, string> = { pilot: 'Pilot', standard: 'Standard', premium: 'Premium' };

type View = 'user' | 'school';

const usd = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function relativeTime(ms: number, now: number): string {
  if (!ms) return '—';
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(ms).toLocaleDateString();
}

// The backend resets monthly_spent_usd lazily, so a row only counts toward
// "this month" when its period_start is in the current calendar month. The
// server's month rolls over on its own clock, so the viewer's local month and
// the UTC month are both accepted; otherwise a UTC+3 user sees $0 for the
// first hours of a month.
function inCurrentMonth(periodStart: string | null | undefined, now: Date): boolean {
  if (!periodStart) return false;
  const local = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const utc = now.toISOString().slice(0, 7);
  const month = periodStart.slice(0, 7);
  return month === local || month === utc;
}

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumSignificantDigits: 3 });
const compact = (n: number) => COMPACT.format(n);

/** "1.84M" -> ["1.84", "M"] so the KPI value can carry the suffix as its muted unit. */
function splitCompact(n: number): [string, string] {
  const t = compact(n);
  const m = t.match(/^([\d.,]+)(.*)$/);
  return m ? [m[1], m[2]] : [t, ''];
}

const MONTH_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
/** "October 2026" from the credits `month_start` (an ISO date). */
function monthLabel(monthStart: string | undefined): string | null {
  if (!monthStart) return null;
  const d = new Date(`${monthStart.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : MONTH_FMT.format(d);
}

/** "12 Oct 2026, 9:22 AM" */
function lastUsageStamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${date}, ${timeOf(d)}`;
}

const SchoolAIUsage: React.FC = () => {
  const { token } = useAuth();
  const [view, setView] = useState<View>('school');
  // Re-render every 30s so "Updated 2 min ago" keeps moving.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin-school-ai-usage'],
    queryFn: () => getAdminSchoolAIUsage(token),
    staleTime: 30_000,
  });

  const usages: AdminSchoolAIUsageRow[] = Array.isArray(data) ? data : [];

  const totals = useMemo(() => {
    let spent = 0, month = 0, input = 0, output = 0;
    for (const u of usages) {
      spent += num(u.total_spent_usd);
      if (inCurrentMonth(u.period_start, new Date())) month += num(u.monthly_spent_usd);
      input += num(u.input_tokens);
      output += num(u.output_tokens);
    }
    return { spent, month, input, output };
  }, [usages, now]);

  const creditsQ = useQuery({
    queryKey: ['admin-ai-credits'],
    queryFn: adminGetAiCredits,
    staleTime: 30_000,
  });
  const credits = creditsQ.data;
  const creditSchools = useMemo(() => credits?.schools ?? [], [credits]);
  // User total for the "% of all users" note; the page works without it.
  const statsQ = useQuery({ queryKey: ['admin-platform-stats'], queryFn: adminGetPlatformStats, staleTime: 60_000 });
  const usersTotal = statsQ.data?.users?.total ?? 0;
  const creditPct = credits && credits.totals.allocation > 0 ? (credits.totals.credits_used / credits.totals.allocation) * 100 : 0;

  const qc = useQueryClient();
  const [allocOpen, setAllocOpen] = useState(false);
  const [planEdits, setPlanEdits] = useState<Record<number, AdminSchoolPlan>>({});
  const [overrideEdits, setOverrideEdits] = useState<Record<number, string>>({});
  const [clearFlags, setClearFlags] = useState<Record<number, boolean>>({});
  const [allocError, setAllocError] = useState<string | null>(null);

  // The credits endpoint only has the effective allocation; the stored override comes from the schools list.
  const schoolsQ = useQuery({
    queryKey: ['admin-schools'],
    queryFn: () => adminGetSchools({ page: 1, page_size: 100000 }),
    staleTime: 5_000,
    enabled: allocOpen,
  });
  const originalOverride = (id: number): number | null => schoolsQ.data?.results.find((x) => x.id === id)?.ai_credit_allocation_override ?? null;

  const openAlloc = () => { setPlanEdits({}); setOverrideEdits({}); setClearFlags({}); setAllocError(null); setAllocOpen(true); };

  const saveAlloc = useMutation({
    mutationFn: async () => {
      // Validate every row first so nothing is sent when any row is invalid.
      const jobs: { name: string; id: number; patch: { plan?: AdminSchoolPlan; ai_credit_allocation?: number | null } }[] = [];
      for (const s of creditSchools) {
        const id = s.institution_id;
        const patch: { plan?: AdminSchoolPlan; ai_credit_allocation?: number | null } = {};
        const plan = planEdits[id];
        if (plan !== undefined) {
          if (!ADMIN_SCHOOL_PLANS.includes(plan)) throw new Error(`Plan for ${s.name} is not valid.`);
          if (plan !== s.plan) patch.plan = plan;
        }
        if (clearFlags[id]) {
          patch.ai_credit_allocation = null;
        } else {
          const raw = (overrideEdits[id] ?? '').trim();
          if (raw) {
            const n = Number(raw);
            if (!Number.isInteger(n) || n < 0) throw new Error(`Override for ${s.name} must be a whole number of credits (0 or more).`);
            if (n !== originalOverride(id)) patch.ai_credit_allocation = n;
          }
        }
        if (Object.keys(patch).length) jobs.push({ name: s.name, id, patch });
      }
      const results = await Promise.allSettled(jobs.map((j) => adminUpdateSchool(j.id, j.patch)));
      const failed = jobs.filter((_, k) => results[k].status === 'rejected').map((j) => j.name);
      return { ok: jobs.length - failed.length, failed };
    },
    onSuccess: ({ ok, failed }) => {
      if (failed.length === 0) setAllocOpen(false);
      else setAllocError(`${ok} saved · ${failed.length} failed: ${failed.join(', ')}`);
    },
    onError: (err) => setAllocError(apiErrorText(err, 'Could not save the allocations.')),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin-ai-credits'] });
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
    },
  });

  const { rows: sortedUsages, sort, toggle } = useSortableTable(usages, USER_COLUMNS, { key: 'user', dir: 'asc' });
  const { rows: sortedSchools, sort: schoolSort, toggle: toggleSchool } = useSortableTable(creditSchools, SCHOOL_COLUMNS, { key: 'school', dir: 'asc' });

  const userPg = usePagination(sortedUsages, 10, `${sort?.key}:${sort?.dir}`);
  const schoolPg = usePagination(sortedSchools, 10, `${schoolSort?.key}:${schoolSort?.dir}`);
  const allocPg = usePagination(creditSchools, 5);

  // The credits endpoint has no student count, so "active" means AI users with usage in the current period.
  const activeAiUsers = useMemo(() => usages.filter((u) => inCurrentMonth(u.period_start, new Date())).length, [usages, now]);
  const perActive = credits && activeAiUsers > 0 ? credits.totals.credits_used / activeAiUsers : null;

  const month = monthLabel(credits?.month_start);
  const overCount = creditSchools.filter((s) => pctUsed(s) >= HIGH_USE).length;
  const subtitle = credits
    ? `${compact(num(credits.totals.credits_used))} of ${compact(num(credits.totals.allocation))} credits used this month · ${usd(num(credits.totals.spent_usd))} spent · ${overCount} ${overCount === 1 ? 'school' : 'schools'} above ${HIGH_USE}% of allocation.`
    : 'Spend and token use by every user with an AI usage record.';
  const [usedNum, usedSuffix] = credits ? splitCompact(num(credits.totals.credits_used)) : ['—', ''];
  const spendTotal = credits ? num(credits.totals.spent_usd) : null;
  const dollars = spendTotal === null ? '—' : `$${Math.trunc(spendTotal).toLocaleString()}`;
  const cents = spendTotal === null ? '' : `.${spendTotal.toFixed(2).split('.')[1]}`;
  const userShare = usersTotal > 0 ? Math.min(100, Math.round((activeAiUsers / usersTotal) * 100)) : null;
  const thClass = 'border-b border-line';

  return (
    <div className="space-y-[18px]">
      <PageHeader
        eyebrow={`Platform admin · AI credits${month ? ` · ${month}` : ''}`}
        title="AI usage"
        subtitle={subtitle}
        actions={
          <>
            <Button icon={<SlidersHorizontal size={16} aria-hidden />} onClick={openAlloc} disabled={creditSchools.length === 0}>Set allocations</Button>
            <span className="text-[13px] text-muted" aria-live="polite">
              {isFetching ? 'Refreshing…' : dataUpdatedAt ? `Updated ${relativeTime(dataUpdatedAt, now)}` : ''}
            </span>
            <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden />} onClick={() => refetch()} disabled={isFetching}>Refresh data</Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-[18px] min-[1181px]:grid-cols-4">
        <KpiCard
          label="Credits used"
          chip={credits && creditPct > 70 ? `${Math.round(creditPct)}%` : undefined}
          value={credits ? (
            <span title={`${num(credits.totals.credits_used).toLocaleString()} / ${num(credits.totals.allocation).toLocaleString()}`}>
              <span aria-hidden="true">{usedNum}</span>
              <span className="sr-only">{num(credits.totals.credits_used).toLocaleString()} of {num(credits.totals.allocation).toLocaleString()} credits</span>
            </span>
          ) : '—'}
          unit={credits ? <span aria-hidden="true">{usedSuffix} / {compact(num(credits.totals.allocation))}</span> : undefined}
          track={credits ? <Track label="Credits used this month" value={creditPct} tone="accent" /> : undefined}
          sub={credits && creditPct <= 70 ? `${Math.round(creditPct)}% of this month's allocation` : undefined}
        />
        <KpiCard
          label="Spend this month"
          value={dollars}
          unit={cents || undefined}
          sub={usages.length ? (
            <span title={`${totals.input.toLocaleString()} in / ${totals.output.toLocaleString()} out`}>
              <span aria-hidden="true">input {compact(totals.input)} · output {compact(totals.output)} tokens, all time</span>
              <span className="sr-only">{totals.input.toLocaleString()} input, {totals.output.toLocaleString()} output tokens, all time</span>
            </span>
          ) : 'This month'}
        />
        <KpiCard
          label="AI users"
          value={usages.length.toLocaleString()}
          sub={`${activeAiUsers.toLocaleString()} active this month${userShare !== null ? ` · ${userShare}% of all users` : ''}`}
        />
        <KpiCard
          label="Per active AI user"
          value={perActive === null ? '—' : Math.round(perActive).toLocaleString()}
          sub={perActive === null ? 'No AI use this month' : `credits · about ${usd(perActive / (credits!.credits_per_usd || 1))}`}
        />
      </div>

      <PanelCard>
        <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
          <Segmented<View>
            label="Breakdown"
            value={view}
            onChange={setView}
            options={[{ key: 'school', label: 'By school' }, { key: 'user', label: 'By user' }]}
          />
          <span className="text-[13px] text-muted">
            {view === 'user' ? `${usages.length.toLocaleString()} users · sorted by username A→Z` : `${creditSchools.length} schools · rows above ${HIGH_USE}% of allocation are highlighted`}
          </span>
        </div>
        {view === 'school' ? (
          creditsQ.isLoading ? <LoadingState label="Loading credits…" />
          : creditsQ.isError ? <ErrorPanel title="Couldn't load credits" body={`Error loading credits: ${(creditsQ.error as any)?.message || 'Unknown error'}`} onRetry={() => creditsQ.refetch()} />
          : creditSchools.length === 0 ? <EmptyState icon={<Bot />} title="No schools yet." body="Schools appear here once they are added." />
          : (
          <>
            <Table aria-label="AI usage by school" minWidth={920}>
              <thead>
                <tr>
                  <SortableTh columnKey="school" sort={schoolSort} onToggle={toggleSchool} className={thClass}>School</SortableTh>
                  <SortableTh columnKey="used" sort={schoolSort} onToggle={toggleSchool} className={`${thClass} w-[26%]`}>Credits used / allocation</SortableTh>
                  <SortableTh columnKey="input_tokens" sort={schoolSort} onToggle={toggleSchool} align="right" className={thClass} help="Lifetime input tokens of the school's users; the server keeps no monthly token figure.">Input tokens</SortableTh>
                  <SortableTh columnKey="output_tokens" sort={schoolSort} onToggle={toggleSchool} align="right" className={thClass} help="Lifetime output tokens of the school's users; the server keeps no monthly token figure.">Output tokens</SortableTh>
                  <SortableTh columnKey="spent" sort={schoolSort} onToggle={toggleSchool} align="right" className={thClass}>Spent (USD)</SortableTh>
                  <SortableTh columnKey="last_usage" sort={schoolSort} onToggle={toggleSchool} className={thClass}>Last usage</SortableTh>
                </tr>
              </thead>
              <tbody>
                {schoolPg.pageItems.map((s) => {
                  const p = pctUsed(s);
                  const high = p >= HIGH_USE;
                  return (
                    <tr key={s.institution_id} data-highlight={high ? 'true' : undefined} className={high ? 'bg-accent-bg/50' : ''}>
                      <Td>
                        <span className="block font-semibold text-ink">{s.name}</span>
                        <span className="block text-xs text-muted">{PLAN_LABEL[s.plan] ?? s.plan} plan</span>
                      </Td>
                      <Td>
                        <span className="flex min-w-[180px] items-center gap-2.5">
                          <Track className="flex-1" label={`Credits used: ${s.name}`} value={p} tone={high ? 'accent' : 'slate'} />
                          <span className="whitespace-nowrap font-mono text-[13px] text-ink" title={`${s.credits_used.toLocaleString()} / ${s.allocation.toLocaleString()}`}>{compact(s.credits_used)} / {compact(s.allocation)}</span>
                          <Chip tone={high ? 'down' : 'neutral'}>{Math.round(p)}%</Chip>
                        </span>
                      </Td>
                      <Td align="right" className="font-mono">{s.input_tokens_total == null ? '—' : compact(s.input_tokens_total)}</Td>
                      <Td align="right" className="font-mono">{s.output_tokens_total == null ? '—' : compact(s.output_tokens_total)}</Td>
                      <Td align="right" className="font-mono font-semibold text-ink">{usd(Number(s.spent_usd) || 0)}</Td>
                      <Td className="whitespace-nowrap text-muted">{lastSeen(s.last_usage)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <PagedFooter divider="list" from={schoolPg.from} to={schoolPg.to} total={schoolPg.total} page={schoolPg.page} pageCount={schoolPg.pageCount} onPage={schoolPg.setPage} label="Schools pagination" />
          </>
          )
        ) : isLoading ? (
          <LoadingState label="Loading usage…" />
        ) : isError ? (
          <ErrorPanel title="Couldn't load usage" body={`Error loading usage: ${(error as any)?.message || 'Unknown error'}`} onRetry={() => refetch()} />
        ) : usages.length === 0 ? (
          <EmptyState icon={<Bot />} title="No AI usage recorded yet." body="Usage appears here once users start using the AI features." />
        ) : (
          <>
            <Table aria-label="AI usage by user" minWidth={860}>
              <thead>
                <tr>
                  <SortableTh columnKey="user" sort={sort} onToggle={toggle} className={thClass}>User</SortableTh>
                  <SortableTh columnKey="email" sort={sort} onToggle={toggle} className={thClass}>Email</SortableTh>
                  <SortableTh columnKey="input_tokens" sort={sort} onToggle={toggle} align="right" className={thClass}>Input tokens</SortableTh>
                  <SortableTh columnKey="output_tokens" sort={sort} onToggle={toggle} align="right" className={thClass}>Output tokens</SortableTh>
                  <SortableTh columnKey="total_spent" sort={sort} onToggle={toggle} align="right" className={thClass}>Total spent (USD)</SortableTh>
                  <SortableTh columnKey="last_usage" sort={sort} onToggle={toggle} className={thClass}>Last usage</SortableTh>
                </tr>
              </thead>
              <tbody>
                {userPg.pageItems.map((u) => (
                  <tr key={u.id}>
                    <Td>
                      <span className="flex min-w-0 items-center gap-3">
                        <Initials name={u.username.length > 1 ? `${u.username[0]} ${u.username[1]}` : u.username} />
                        <span className="font-semibold text-ink">{u.username}</span>
                      </span>
                    </Td>
                    <Td className="text-muted">{u.email}</Td>
                    <Td align="right" className="font-mono">{num(u.input_tokens).toLocaleString()}</Td>
                    <Td align="right" className="font-mono">{num(u.output_tokens).toLocaleString()}</Td>
                    <Td align="right" className="font-mono font-semibold text-ink">${num(u.total_spent_usd).toFixed(6)}</Td>
                    <Td className="whitespace-nowrap text-muted">{lastUsageStamp(u.updated)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <PagedFooter divider="list" from={userPg.from} to={userPg.to} total={userPg.total} page={userPg.page} pageCount={userPg.pageCount} onPage={userPg.setPage} label="Users pagination" />
          </>
        )}
      </PanelCard>

      <Modal
        isOpen={allocOpen}
        onClose={() => setAllocOpen(false)}
        title="Set allocations"
        size="lg"
        footer={(
          <>
            <Button variant="ghost" size="sm" onClick={() => setAllocOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={() => { setAllocError(null); saveAlloc.mutate(); }} loading={saveAlloc.isPending}>Save allocations</Button>
          </>
        )}
      >
        <div className="space-y-3">
          <p className="text-[13px] text-muted">Change a plan or enter a monthly credit override. Leave an override blank to keep it as it is.</p>
          {allocPg.pageItems.map((s) => (
            <div key={s.institution_id} className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto_180px_auto]">
              <span className="font-semibold text-ink">{s.name}</span>
              <FormField label="Plan">
                <Segmented<AdminSchoolPlan>
                  label={`Plan for ${s.name}`}
                  dense
                  value={planEdits[s.institution_id] ?? s.plan}
                  onChange={(p) => setPlanEdits((m) => ({ ...m, [s.institution_id]: p }))}
                  options={ADMIN_SCHOOL_PLANS.map((p) => ({ key: p, label: PLAN_LABEL[p] }))}
                />
              </FormField>
              <FormField label="Override" htmlFor={`alloc-override-${s.institution_id}`}>
                <input
                  id={`alloc-override-${s.institution_id}`}
                  aria-label={`Credit override for ${s.name}`}
                  inputMode="numeric"
                  placeholder={s.allocation.toLocaleString()}
                  value={clearFlags[s.institution_id] ? '' : overrideEdits[s.institution_id] ?? (originalOverride(s.institution_id) != null ? String(originalOverride(s.institution_id)) : '')}
                  onChange={(e) => { setClearFlags((m) => ({ ...m, [s.institution_id]: false })); setOverrideEdits((m) => ({ ...m, [s.institution_id]: e.target.value })); }}
                  className="w-full rounded-[12px] border border-line-strong bg-surface px-3 py-2 text-sm text-ink"
                />
              </FormField>
              <Button variant="ghost" size="sm" aria-pressed={!!clearFlags[s.institution_id]} aria-label={`Use plan default for ${s.name}`}
                onClick={() => { setClearFlags((m) => ({ ...m, [s.institution_id]: true })); setOverrideEdits((m) => ({ ...m, [s.institution_id]: '' })); }}>
                Use plan default
              </Button>
            </div>
          ))}
          <PagedFooter from={allocPg.from} to={allocPg.to} total={allocPg.total} page={allocPg.page} pageCount={allocPg.pageCount} onPage={allocPg.setPage} label="Allocations pagination" />
          {allocError && <p role="alert" className="text-xs text-danger-ink">{allocError}</p>}
        </div>
      </Modal>
    </div>
  );
};

export default SchoolAIUsage;
